import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Check } from "lucide-react";
import "./DimsumAvailabilityPage.css";

import progressIcon from "../../assets/progressIcon7.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationsIcon from "../../assets/availability7.png";

import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";

import { useGlobalAlert } from "../../context/AlertContext";
import { updateUser, updateFcmToken } from "../../services/userService";
import { updateDogProfile } from "../../services/dogService";
import { fetchUserData } from "../../redux/userSlice";

const DimsumAvailabilityPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { triggerAlert } = useGlobalAlert();

  const [isLoading, setIsLoading] = useState(false);
  const fcmToken = useSelector((state) => state.user.fcmToken);
  const profile = useSelector((state) => state.user.profile);

  const dogName = formData.dogName || "Dog";
  const formattedDogName =
    dogName.charAt(0).toUpperCase() + dogName.slice(1).toLowerCase();

  const [mobileStep, setMobileStep] = useState(
    location.state?.returnToStep || 1,
  );

  const [availability, setAvailability] = useState(formData.availability || []);

  const availabilityOptions = [
    { id: "mornings", label: "Mornings", icon: morningIcon, large: false },
    {
      id: "afternoons",
      label: "Afternoons",
      icon: afternoonIcon,
      large: false,
    },
    { id: "evenings", label: "Evenings", icon: eveningIcon, large: false },
    { id: "weekdays", label: "Weekdays", icon: weekedaysIcon, large: true },
    { id: "weekends", label: "Weekends", icon: weekenedsIcon, large: true },
    { id: "vacations", label: "Vacations", icon: vacationsIcon, large: false },
  ];

  const pastimeOptions = [
    "🚶 Walks & exploring",
    "🌲 Hiking & adventures",
    "🏃 Running & fitness",
    "🎾 Playing & fetch",
    "🐕 Dog parks & playdates",
    "🏖️ Beach & swimming",
    "🚗 Car rides & day trips",
    "☕ Cafés & hanging out",
    "🛋️ Cuddling & relaxing",
  ];

  useEffect(() => {
    const existingHobbies =
      formData.selectedHobbies || formData.dogHobbies || formData.hobbies;
    if (existingHobbies && Array.isArray(existingHobbies)) {
      const custom = existingHobbies.find((h) => !pastimeOptions.includes(h));
      setFormData((prev) => ({
        ...prev,
        selectedHobbies: existingHobbies,
        dogHobbies: existingHobbies,
        customPastime: custom || prev.customPastime || "",
      }));
    }

    if (formData.availability && Array.isArray(formData.availability)) {
      setAvailability(formData.availability);
    }
  }, []);

  const toggleAvailability = (id) => {
    setAvailability((prev) => {
      let updated;
      if (prev.includes(id)) {
        updated = prev.filter((item) => item !== id);
      } else {
        updated = [...prev, id];
      }
      setFormData((formDataPrev) => ({
        ...formDataPrev,
        availability: updated,
      }));
      return updated;
    });
  };

  const toggleItem = (item, field) => {
    const currentList = formData[field] || [];
    const isSelected = currentList.includes(item);

    // Limit to 3 choices
    if (!isSelected && currentList.length >= 3) {
      if (triggerAlert) {
        triggerAlert("You can only select up to 3 hobbies.", "warning");
      }
      return;
    }

    const newList = isSelected
      ? currentList.filter((i) => i !== item)
      : [...currentList, item];

    setFormData((prev) => ({
      ...prev,
      [field]: newList,
      dogHobbies: newList,
    }));
  };

  const handleCustomWriteOwn = (value, listField, prevValueField) => {
    const currentList = formData[listField] || [];
    const oldValue = formData[prevValueField] || "";

    let updatedList = currentList.filter((item) => item !== oldValue);

    if (value.trim() !== "") {
      // Limit custom input addition to the max 3 rule
      if (!oldValue && updatedList.length >= 3) {
        if (triggerAlert) {
          triggerAlert("You can only select up to 3 hobbies.", "warning");
        }
        return;
      }
      updatedList = [...updatedList, value];
    }

    setFormData((prev) => ({
      ...prev,
      [listField]: updatedList,
      dogHobbies: updatedList,
      [prevValueField]: value,
    }));
  };

  const hasAvailability = availability.length >= 1;
  const hasHobbies =
    (formData.selectedHobbies || []).length >= 1 ||
    formData.customPastime?.trim().length > 0;

  const isStep1Valid = hasAvailability;
  const isStep2Valid = hasHobbies;
  const isFinalNextEnabled = hasAvailability && hasHobbies;

  const handleDesktopNext = () => {
    if (isFinalNextEnabled) navigate("/exp-with-dogs");
  };

  return (
    <div
      className={`da-container ${mobileStep === 1 ? "da-mobile-step-1" : "da-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Availability Selection (Mobile Step 1) */}
      <div
        className={`da-panel da-left-panel ${mobileStep === 1 ? "da-mobile-active" : "da-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(2)}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/team-paws-personality")}
          backColor="#000"
          showProgress={true}
          progressIconSrc={progressIcon}
          progressPercent={70}
          iconLeft={63}
          iconWidth="80px"
        />

        <div className="da-content-wrapper">
          <div className="da-header-section mobile-only">
            <div className="da-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="da-progress-icon"
              />
            </div>
            <div className="da-progress-track">
              <div className="da-progress-fill da-step-1-fill"></div>
            </div>
          </div>

          <h2 className="da-title">
            WHAT IS {dogName.toUpperCase()}'S AVAILABILITY?
          </h2>
          <p className="da-subtitle">
            Generally, when can others request walks or playdates with {dogName}
            ? Can be pupdated anytime.
          </p>

          <div className="da-avail-grid">
            {availabilityOptions.map((opt) => (
              <div
                key={opt.id}
                className={`da-avail-card ${availability.includes(opt.id) ? "selected" : ""}`}
                onClick={() => toggleAvailability(opt.id)}
              >
                <div className="da-icon-wrapper">
                  <img
                    src={opt.icon}
                    alt={opt.label}
                    className={opt.large ? "da-icon-lg" : "da-icon-sm"}
                  />
                </div>
                <span className="da-avail-label">{opt.label}</span>
              </div>
            ))}
          </div>

          <MobileBottomBar
            onNext={() => navigate("/exp-with-dogs")}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Pastimes (Mobile Step 2) */}
      <div
        className={`da-panel da-right-panel ${mobileStep === 2 ? "da-mobile-active" : "da-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() =>
            navigate("/team-paws-personality", { state: { returnToStep: 1 } })
          }
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div className="da-content-wrapper da-right-content">
          <div className="da-header-section da-mobile-only">
            <div className="da-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="da-progress-icon"
              />
            </div>
            <div className="da-progress-track">
              <div className="da-progress-fill da-step-2-fill"></div>
            </div>
          </div>

          <div className="da-text-group">
            <h1
              className="da-title da-text-white"
              style={{ textAlign: "center" }}
            >
              WHAT ARE {dogName.toUpperCase()}'S TOP 3 HOBBIES?
            </h1>
            <p
              className="da-subtitle da-text-white-sub"
              style={{ marginBottom: "1rem" }}
            >
              What would {formattedDogName}'s hobby list look like if he could
              only pick a few?
            </p>
            <p
              className="da-subtitle da-text-white-sub"
              style={{ textAlign: "center", marginBottom: "1.5rem" }}
            >
              Choose up to 3.
            </p>
          </div>

          <div className="da-list-container">
            {pastimeOptions.map((pastime, index) => {
              const isChecked = (formData.selectedHobbies || []).includes(
                pastime,
              );
              return (
                <div
                  key={index}
                  className="da-option-row da-border-light"
                  onClick={() => toggleItem(pastime, "selectedHobbies")}
                >
                  <span className="da-option-label da-text-white">
                    {pastime}
                  </span>
                  <div className={`da-checkbox ${isChecked ? "checked" : ""}`}>
                    {isChecked && (
                      <Check size={18} strokeWidth={4} color="#000" />
                    )}
                  </div>
                </div>
              );
            })}

            <div
              className="da-write-own-box da-bg-light-blue"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingRight: "15px",
              }}
            >
              <input
                type="text"
                placeholder="Other: ______"
                className="da-write-own-input"
                value={formData.customPastime || ""}
                onChange={(e) =>
                  handleCustomWriteOwn(
                    e.target.value,
                    "selectedHobbies",
                    "customPastime",
                  )
                }
              />
              {formData.customPastime?.trim() && (
                <div className="da-checkbox checked">
                  <Check size={18} strokeWidth={4} color="#000" />
                </div>
              )}
            </div>
          </div>

          <DesktopBottomBar
            onNext={handleDesktopNext}
            isNextEnabled={isFinalNextEnabled}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => navigate("/exp-with-dogs")}
            isLoading={isLoading}
            variant="green"
          />

          <MobileBottomBar
            onNext={() => setMobileStep(1)}
            isNextEnabled={isStep2Valid}
            nextIconSrc={nextIcon}
            showSkip={true}
            onSkip={() => setMobileStep(1)}
            isLoading={isLoading}
            variant="green"
          />
        </div>
      </div>
    </div>
  );
};

export default DimsumAvailabilityPage;
